import type { FunctionComponent } from 'react';
import { Fragment, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';

import { useApplicationContext } from '~/shared/context/use-application-context';
import { useTranslate } from '~/shared/context/use-translate';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuGroup,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '~/ui/core/dropdown-menu/DropdownMenu';
import { IconThreeDots } from '~/ui/core/icons';
import { SideNavigationRow } from '~/ui/domain/side-navigation';

import type { NavigationItem } from './navigation-items';
import { isNavigationItemActive, navigationItemKey } from './navigation-items';

/** A scope's entry set aside behind "…": a top-level row, or a group with all its pages. */
export type NavigationMoreEntry =
    | { readonly item: NavigationItem; readonly kind: 'row' }
    | {
          readonly Icon: NavigationItem['Icon'];
          readonly items: readonly NavigationItem[];
          readonly key: string;
          readonly kind: 'group';
          readonly label: string;
          readonly landing: NavigationItem | undefined;
      };

const MoreItem: FunctionComponent<{
    readonly Icon?: NavigationItem['Icon'];
    readonly item: NavigationItem;
    readonly label?: string;
}> = ({ Icon, item, label = item.menuLabel }) => {
    const { actions } = useApplicationContext();
    const navigate = useNavigate();

    return (
        <DropdownMenuItem
            onSelect={() => (item.externalUrl ? actions.openWindow(item.path) : void navigate(item.path))}
        >
            {Icon === undefined ? <span aria-hidden={true} className="size-4 shrink-0" /> : <Icon size={16} />}
            <span className="min-w-0 flex-1 truncate">{label}</span>
        </DropdownMenuItem>
    );
};

const allItems = (entry: NavigationMoreEntry): readonly NavigationItem[] =>
    entry.kind === 'row' ? [entry.item] : [...(entry.landing === undefined ? [] : [entry.landing]), ...entry.items];

// The rail's last entry when it has no room for everything: "…" opens a menu with
// what was set aside, in rail order, a group under its own heading. It reads as
// pressed while its menu is open or the current page is among them, as a folded group does.
export const NavigationMoreMenu: FunctionComponent<{
    readonly entries: readonly NavigationMoreEntry[];
    readonly expanded: boolean;
    readonly links: readonly NavigationItem[];
}> = ({ entries, expanded, links }) => {
    const translate = useTranslate();
    const { pathname } = useLocation();
    const [open, setOpen] = useState(false);
    const active = [...entries.flatMap(allItems), ...links].some(
        (item) => !item.externalUrl && isNavigationItemActive(pathname, item),
    );

    return (
        <DropdownMenu onOpenChange={setOpen} open={open}>
            <DropdownMenuTrigger asChild={true}>
                <SideNavigationRow
                    active={active || open}
                    expanded={expanded}
                    Icon={IconThreeDots}
                    label={translate('common.more')}
                />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="min-w-56" side="right" sideOffset={10}>
                {entries.map((entry, index) => {
                    const separator = index > 0 && (entry.kind === 'group' || entries[index - 1]?.kind === 'group');

                    if (entry.kind === 'row') {
                        return (
                            <Fragment key={navigationItemKey(entry.item)}>
                                {separator ? <DropdownMenuSeparator /> : null}
                                <MoreItem Icon={entry.item.Icon} item={entry.item} />
                            </Fragment>
                        );
                    }

                    return (
                        <Fragment key={entry.key}>
                            {separator ? <DropdownMenuSeparator /> : null}
                            <DropdownMenuGroup aria-label={entry.label}>
                                {entry.landing === undefined ? (
                                    <DropdownMenuLabel>{entry.label}</DropdownMenuLabel>
                                ) : (
                                    <MoreItem Icon={entry.Icon} item={entry.landing} label={entry.label} />
                                )}
                                {entry.items.map((item) => (
                                    <MoreItem key={navigationItemKey(item)} item={item} />
                                ))}
                            </DropdownMenuGroup>
                        </Fragment>
                    );
                })}
                {links.length > 0 && entries.length > 0 ? <DropdownMenuSeparator /> : null}
                {links.map((item) => (
                    <MoreItem key={navigationItemKey(item)} Icon={item.Icon} item={item} />
                ))}
            </DropdownMenuContent>
        </DropdownMenu>
    );
};
